import { supabase } from '@/features/sistema/services/supabaseClient';

const THREADS_TABLE = 'app_comment_threads';
const THREADS_LATEST_VIEW = 'v_app_threads_latest';
const THREADS_UNREAD_VIEW = 'v_app_threads_unread';
const ENSURE_THREAD_RPC = 'fn_app_comment_thread_ensure';

export type CommentThreadRecord = {
  id: string;
  obra_id: string | null;
  recurso: string;
  recurso_id: string;
  created_at: string;
  created_by: string | null;
  last_at: string | null;
  last_created_by?: string | null;
  last_snippet?: string | null;
};

export type CommentThreadLatestViewRecord = {
  id: string;
  obra_id: string | null;
  recurso: string | null;
  recurso_id: string | null;
  last_at: string;
  last_comment_at: string | null;
  last_comment_by: string | null;
  unread: boolean | null;
  is_unread?: boolean | null;
  has_mention: boolean | null;
  last_created_by?: string | null;
  last_snippet?: string | null;
};

export type CommentThreadUnreadViewRecord = {
  id: string;
  obra_id: string | null;
  mencoes: string[] | null;
  unread: boolean | null;
};

export async function getThreadByResource(recurso: string, recursoId: string) {
  if (!recurso || !recursoId) {
    return null as CommentThreadRecord | null;
  }

  const { data, error } = await supabase
    .from(THREADS_TABLE)
    .select('*')
    .eq('recurso', recurso)
    .eq('recurso_id', recursoId)
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data as CommentThreadRecord | null;
}

export async function getThreadById(threadId: string) {
  if (!threadId) {
    return null as CommentThreadRecord | null;
  }

  const { data, error } = await supabase
    .from(THREADS_TABLE)
    .select('*')
    .eq('id', threadId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data as CommentThreadRecord | null;
}

export async function getThreadLastAt(threadId: string) {
  if (!threadId) {
    return null;
  }

  const { data, error } = await supabase
    .from(THREADS_TABLE)
    .select('last_at')
    .eq('id', threadId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  const row = data as { last_at: string | null } | null;
  return row?.last_at ?? null;
}

type ListLatestParams = {
  obraId: string;
  search?: string | null;
  onlyUnread?: boolean;
  limit?: number;
  offset?: number;
};

export async function listLatestThreadsByObra({
  obraId,
  search,
  onlyUnread,
  limit = 50,
  offset = 0,
}: ListLatestParams) {
  if (!obraId) {
    return [] as CommentThreadLatestViewRecord[];
  }

  let query = supabase
    .from(THREADS_LATEST_VIEW)
    .select('*')
    .eq('obra_id', obraId)
    .order('last_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (search && search.trim()) {
    query = query.ilike('recurso_id', `%${search.trim()}%`);
  }

  if (onlyUnread) {
    query = query.eq('unread', true);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data ?? []) as CommentThreadLatestViewRecord[];
}

export async function listUnreadThreadsByObra(obraId: string) {
  if (!obraId) {
    return [] as CommentThreadUnreadViewRecord[];
  }

  const { data, error } = await supabase
    .from(THREADS_UNREAD_VIEW)
    .select('*')
    .eq('obra_id', obraId);

  if (error) {
    throw error;
  }

  return (data ?? []) as CommentThreadUnreadViewRecord[];
}

export async function ensureThread(params: {
  obraId?: string | null;
  recurso: string;
  recursoId: string;
  userId?: string | null;
}) {
  const { obraId = null, recurso, recursoId, userId = null } = params;

  const { data, error } = await supabase.rpc(ENSURE_THREAD_RPC, {
    p_obra_id: obraId,
    p_recurso: recurso,
    p_recurso_id: recursoId,
    p_user_id: userId,
  });

  if (error) {
    throw error;
  }

  return (data as string | null) ?? null;
}

